import "styles/sections/reviews/Slider.scss";
import { useState } from "react";
import { useSwipeable } from "react-swipeable";
import { LangProp, Review, SliderData } from "types";
import { slugify } from "utils";
import { SliderArrowLeft, SliderArrowRight } from "images-react";
import { general } from "translations";
import watercolor from "images-react/backgrounds/watercolor.png";
import SlideItem from "./SlideItem";
import SlideNavButton from "./SlideNavButton";
import { useParallax } from "react-scroll-parallax";

function Slider({ data, lang }: Props) {
  const { moreLabel, lessLabel } = general[lang];
  const {
    sliderLabel,
    prevButtonLabel,
    nextButtonLabel,
    liveRegionLabels,
    currentSlideLabel,
    slides,
  } = data;
  const headingId = slugify(sliderLabel);
  const [currentSlide, setCurrentSlide] = useState(1);
  const numberOfSlides = slides.length;
  const isLastSlide = currentSlide === numberOfSlides;
  const isFirstSlide = currentSlide === 1;

  const prevSlide = () => {
    if (currentSlide === 1) return;
    setCurrentSlide((c) => --c);
  };

  const nextSlide = () => {
    if (currentSlide === numberOfSlides) return;
    setCurrentSlide((c) => ++c);
  };

  const handleSlideChange = (slideNumber: number) => {
    setCurrentSlide(slideNumber);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => prevSlide(),
  });

  const { ref: hillsRef } = useParallax<HTMLImageElement>({
    speed: -5,
  });

  return (
    <section className="slider" aria-labelledby={headingId}>
      <div className="slider__background">
        <img
          className="slider__background-image"
          src={watercolor}
          alt=""
          ref={hillsRef}
        />
      </div>

      <h3 className="visually-hidden" id={headingId}>
        {sliderLabel}
      </h3>

      <div className="slider__slides-container">
        <ul className="slider__slides" {...swipeHandlers}>
          {slides.map((slide, i) => (
            <SlideItem
              key={slide.id}
              data={slide}
              slideNumber={i + 1}
              currentSlide={currentSlide}
              label={{ more: moreLabel, less: lessLabel }}
              lang={lang}
            />
          ))}
        </ul>

        <ul className="slider__controls controls">
          <li className="controls__item controls__item--prev">
            <button
              className="controls__button controls__button--prev"
              aria-label={prevButtonLabel}
              onClick={prevSlide}
              disabled={isFirstSlide}
            >
              <SliderArrowLeft
                className="controls__arrow controls__arrow--left"
                role="presentation"
                aria-hidden="true"
              />
              <SliderArrowLeft
                className="controls__arrow controls__arrow--left"
                role="presentation"
                aria-hidden="true"
              />
            </button>
          </li>
          <li className="controls__item controls__item--next">
            <button
              className="controls__button controls__button--next"
              aria-label={nextButtonLabel}
              onClick={nextSlide}
              disabled={isLastSlide}
            >
              <SliderArrowRight
                className="controls__arrow controls__arrow--right"
                role="presentation"
                aria-hidden="true"
              />
              <SliderArrowRight
                className="controls__arrow controls__arrow--right"
                role="presentation"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </div>

      <ul className="slider__nav">
        {slides.map((_, i) => (
          <SlideNavButton
            key={i}
            slideNumber={i + 1}
            slideLabel={liveRegionLabels[0]}
            currentSlide={currentSlide}
            currentSlideLabel={currentSlideLabel}
            onSlideChange={handleSlideChange}
          />
        ))}
      </ul>

      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {liveRegionLabels[0]} {currentSlide} {liveRegionLabels[1]}{" "}
        {numberOfSlides}.
      </div>
    </section>
  );
}

interface Props extends LangProp {
  data: SliderData<Review>;
}

export default Slider;
